import { Block, Flex, Icon, ProductColor, Typography } from '@aloft/toolkit-sui';
import { Progress } from '@aloft/toolkit-sui/chakra';

import { ChangeEvent, DragEvent, useCallback, useRef, useState } from 'react';

import { FileUploaderFileItem } from './FileUploaderFileItem.tsx';

const getBorderColor = (isDisabled: boolean, isDragActive: boolean): ProductColor => {
  if (isDisabled) return 'strokeDefault';
  if (isDragActive) return 'buttonActive';
  return 'buttonDefault';
};

type Accept = Record<string, string[]>;

type FileUploaderFile = {
  name: string;
  id: string;
  blobID?: string;
  isError?: boolean;
  isSuccess?: boolean;
  previewUrl?: string;
};

type FileUploaderProps = {
  files: FileUploaderFile[];
  onFileAdd: (file: File, result: string, isError?: boolean) => void;
  onFileDelete?: (id: string) => void;
  onFileDownload?: (id: string) => void;
  isLoading?: boolean;
  loadingMessage?: string;
  isDisabled?: boolean;
  maxFiles?: number;
  dropzoneMessage?: string;
  showProgressBar?: boolean;
  accept?: Accept;
  error?: string;
  warn?: string;
  id?: string;
  readAs?: 'text' | 'dataURL' | 'binaryString' | 'none';
};

export const FileUploader = ({
  files,
  isDisabled = false,
  isLoading = false,
  loadingMessage = 'Uploading',
  maxFiles = 1,
  dropzoneMessage = 'Click to browse, or drag and drop',
  showProgressBar = false,
  accept,
  error,
  warn,
  id,
  onFileAdd,
  onFileDelete,
  onFileDownload,
  readAs = 'text',
}: FileUploaderProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const acceptAttr = accept
    ? [...new Set([...Object.keys(accept), ...Object.values(accept).flat()])].join(',')
    : undefined;

  const handleFile = useCallback(
    (file?: File) => {
      if (!file) return;

      if (readAs === 'none') {
        onFileAdd(file, '', false);
        return;
      }

      const onLoadEnd = (loadedFile: File, ev: ProgressEvent<FileReader>) => {
        const result = ev.target?.result;
        if (typeof result !== 'string') throw new Error('Unexpected file read result');
        onFileAdd(loadedFile, result);
      };

      const reader = new FileReader();
      reader.addEventListener('loadend', (e) => onLoadEnd(file, e));
      reader.addEventListener('error', console.error);

      switch (readAs) {
        case 'dataURL':
          reader.readAsDataURL(file);
          break;
        case 'binaryString':
          reader.readAsBinaryString(file);
          break;
        default:
          reader.readAsText(file);
          break;
      }
    },
    [onFileAdd, readAs],
  );

  const onInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      handleFile(event.target.files?.[0]);
      event.target.value = '';
    },
    [handleFile],
  );

  const onDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragActive(false);
      if (isDisabled || isLoading) return;
      handleFile(event.dataTransfer.files?.[0]);
    },
    [handleFile, isDisabled, isLoading],
  );

  const borderColor = getBorderColor(isDisabled, isDragActive);

  return (
    <Flex height="100%" width="100%" flexDirection="column" gap={1}>
      <Block
        flexGrow={1}
        borderColor={borderColor}
        borderStyle="dashed"
        borderWidth={isDragActive ? '2px' : '1px'}
        backgroundColor={isDisabled ? 'disabledFill' : 'selected1'}
        borderRadius="6px"
        cursor={isDisabled || isLoading ? 'not-allowed' : 'pointer'}
      >
        {isLoading && showProgressBar ? (
          <Flex
            flexDirection="column"
            height="100%"
            width="100%"
            alignItems="center"
            justifyContent="center"
            gap={4}
          >
            <Progress isIndeterminate width="100%" />
            <Typography variant="h4" color="textSecondary">
              {loadingMessage}
            </Typography>
          </Flex>
        ) : (
          <div
            style={{ height: '100%', width: '100%' }}
            onClick={() => {
              if (!isDisabled && !isLoading) inputRef.current?.click();
            }}
            onDragOver={(event) => {
              event.preventDefault();
              if (!isDisabled && !isLoading) setIsDragActive(true);
            }}
            onDragLeave={() => setIsDragActive(false)}
            onDrop={onDrop}
          >
            <input
              ref={inputRef}
              id={id}
              type="file"
              hidden
              accept={acceptAttr}
              disabled={isDisabled || isLoading || files.length >= maxFiles}
              onChange={onInputChange}
            />
            <Flex
              width="100%"
              height="100%"
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
              gap={4}
            >
              {files.length < maxFiles ? (
                <>
                  <Icon
                    icon="cloud-upload"
                    size="xxl"
                    color="buttonDefault"
                    __styleDoNotUse={{ opacity: 0.3 }}
                  />
                  <Typography variant="h4">{dropzoneMessage}</Typography>
                </>
              ) : null}
              {files.map((f) => (
                <FileUploaderFileItem
                  key={f.id}
                  blobID={f.blobID}
                  filename={f.name}
                  previewUrl={f.previewUrl}
                  {...(onFileDownload && {
                    onDownloadClick: () => onFileDownload(f.id),
                  })}
                  {...(onFileDelete && {
                    onDeleteClick: () => onFileDelete(f.id),
                  })}
                  isError={f.isError}
                  isDisabled={isDisabled}
                  isSuccess={f.isSuccess}
                />
              ))}
            </Flex>
          </div>
        )}
      </Block>
      {error && (
        <Typography variant="body2" color="textError">
          {error}
        </Typography>
      )}
      {warn && <Typography variant="body2">{warn}</Typography>}
    </Flex>
  );
};
